import React, { useRef, useState } from 'react';
import '../../styles/components/profile_page/UploadPhotosModal.css';

const MAX_FILES = 10;

const UploadPhotosModal = ({ isOpen, onClose, onUpload }) => {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [albumName, setAlbumName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (selectedFiles.length > MAX_FILES) {
      setFiles([]);
      setError(`You can upload a maximum of ${MAX_FILES} images at once.`);
      e.target.value = '';
      return;
    }

    setFiles(selectedFiles);
    setError('');
  };

  const handleAlbumNameChange = (e) => {
    setAlbumName(e.target.value);
    setError('');
  };

  const handleSubmit = async () => {
    const arr = Array.from(files || []);

    if (!arr.length) {
      setError('Please choose at least one image.');
      return;
    }

    try {
      setIsUploading(true);
      setError('');

      await onUpload(arr, albumName.trim());

      onClose();
    } catch (err) {
      console.error('[UploadPhotosModal] Upload failed:', err);
      setError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const fileSummary = files.length
    ? `${files.length} ${files.length === 1 ? 'photo' : 'photos'} selected`
    : 'JPEG, PNG or WebP · up to 10 photos';

  return (
    <div className="upload-photos-overlay" onMouseDown={(e) => {
      if (e.target === e.currentTarget && !isUploading) onClose();
    }}>
      <div className="upload-modal" role="dialog" aria-modal="true" aria-labelledby="upload-photos-title">
        <button
          type="button"
          className="upload-modal__close"
          onClick={onClose}
          disabled={isUploading}
          aria-label="Close upload photos"
        >
          ×
        </button>

        <div className="upload-modal__header">
          <h2 id="upload-photos-title">Upload photos</h2>
          <p>Add images to your portfolio, with an optional album name.</p>
        </div>

        <div className="upload-modal__field">
          <span className="upload-modal__label">Photos</span>
          <input
            ref={fileInputRef}
            className="upload-modal__file-input"
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
            disabled={isUploading}
          />
          <button
            type="button"
            className="upload-modal__file-picker"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <span className="upload-modal__file-picker-icon" aria-hidden="true">＋</span>
            <span>
              <strong>{files.length ? 'Choose different photos' : 'Choose photos'}</strong>
              <small>{fileSummary}</small>
            </span>
          </button>
        </div>

        <div className="upload-modal__field">
          <label className="upload-modal__label" htmlFor="upload-album-name">
            Album name <span>Optional</span>
          </label>
          <input
            id="upload-album-name"
            className="upload-modal__text-input"
            type="text"
            placeholder="e.g. Weddings, Portraits, Events"
            value={albumName}
            onChange={handleAlbumNameChange}
            disabled={isUploading}
          />
        </div>

        {error && <p className="upload-modal__message upload-modal__message--error">{error}</p>}
        {isUploading && <p className="upload-modal__message">Uploading your photos…</p>}

        <div className="upload-modal__actions">
          <button
            type="button"
            className="upload-modal__cancel"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="upload-modal__submit"
            onClick={handleSubmit}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading…' : 'Upload photos'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadPhotosModal;
